import React from 'react';
import { connect } from 'react-redux';

import { RootState } from 'MyTypes';
import pick from 'lodash/pick';

import { parseApiError } from '@portal/common/api/request';
import { captureException, captureUnhandledError } from '@portal/common/services/error-tracking';
import { AppStateProvider, localStorageAdapter } from '@portal/common/services/local-storage';
import { showSentryReportDialog } from '@portal/common/services/sentry';

import api from '../../api';
import config from '../../config';
import Router from '../../router';
import { createStore } from '../../store';
import {
    getIsRequiredDataLoaded,
    getSessionAccount,
    getSessionAccountId,
} from '../../store/root-reducer';
import { destroySession, loadAccount } from '../../store/session/actions';

const mapStateToProps = (state: RootState) => ({
    accountId: getSessionAccountId(state),
    sessionAccount: getSessionAccount(state),
    isRequiredDataLoaded: getIsRequiredDataLoaded(state),
});

const dispatchProps = { destroySession, loadAccount };

type Props = ReturnType<typeof mapStateToProps> & typeof dispatchProps;
type State = { error: object | null; eventId?: string };

class Root extends React.PureComponent<Props, State> {
    state: State = { error: null };

    componentDidMount() {
        const { accountId, loadAccount, destroySession } = this.props;

        // set global error handler
        api.setGlobalErrorHandler((error) => {
            const apiError = parseApiError(error);

            // if invalid session destroy the user session
            if (apiError?.code === 401 && apiError?.message === 'Invalid session') {
                destroySession({ error: 'Session expired. Please Sign In again.' });
            } else {
                captureException(error);
            }

            // it should always throw parsedError to be then catched by consumer handler
            // because as global handler is invoked before consumer then handler
            throw apiError;
        });

        if (accountId) {
            // Load the latest account info
            loadAccount(accountId);
        }
    }

    componentDidCatch(error, info) {
        // console.log('Unhandled component error', err, info);
        this.setState({ error });
        const callback = (eventId: string) => {
            this.setState({ eventId });
        };
        captureUnhandledError(error, info, callback);
    }

    render() {
        const { accountId, sessionAccount, isRequiredDataLoaded } = this.props;

        if (accountId && (sessionAccount == null || !isRequiredDataLoaded)) {
            return null;
        }

        if (this.state.error) {
            // render fallback UI
            return (
                <button
                    type="button"
                    onClick={() => showSentryReportDialog({ eventId: this.state.eventId })}
                >
                    Report feedback
                </button>
            );
        } else {
            return <Router />;
        }
    }
}

const ConnectedRoot = connect(mapStateToProps, dispatchProps)(Root);

const ConnectedRootWithStateProvider = () => (
    <AppStateProvider<RootState>
        version={config.storageStateVersion}
        stateKey="redux"
        storageAdapter={localStorageAdapter}
        createStore={createStore}
        onInitialState={({ state }) => {
            if (state && state.session && state.session.token) {
                api.setSessionToken(state.session.token);
            }
        }}
        onStateUpdate={({ state, setState }) => {
            setState(pick(state, ['session']));
        }}
    >
        <ConnectedRoot />
    </AppStateProvider>
);

export default ConnectedRootWithStateProvider;
